import React from "react";
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";

interface PolicyAction {
    id: string;
    label: string;
    params?: any;
    followup_message?: string;
}

interface BillingDetailsCardProps {
    card: any;
    onAction?: (action: PolicyAction) => void;
}

const BillingDetailsCard: React.FC<BillingDetailsCardProps> = ({ card, onAction }) => {
    if (!card) return null;

    const handleItemAction = (action?: PolicyAction) => {
        if (!action) return;
        onAction?.(action);
    };

    const renderSection = (section: any, title: string) => {
        if (!section || !section.items || section.items.length === 0) return null;

        return (
            <Box sx={{ mt: 2 }}>
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: 14, md: 14 },
                        color: "#374151",
                        mb: 1,
                    }}
                >
                    {title}
                </Typography>
                
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 1,
                        overflow: "hidden",
                    }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: "#f9fafb" }}>
                                {title === "Invoices" ? (
                                    <>
                                        <TableCell sx={{ fontWeight: 700, fontSize: { xs: 12, md: 14 }, color: "#374151" }}>
                                            Invoice Date
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: { xs: 12, md: 14 }, color: "#374151" }}>
                                            Due Date
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: { xs: 12, md: 14 }, color: "#374151" }}>
                                            Invoice Amount
                                        </TableCell>
                                    </>
                                ) : (
                                    <>
                                        <TableCell sx={{ fontWeight: 700, fontSize: { xs: 12, md: 14 }, color: "#374151" }}>
                                            Paid Date
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: { xs: 12, md: 14 }, color: "#374151" }}>
                                            Paid Amount
                                        </TableCell>
                                    </>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {section.items.map((item: any, idx: number) => {
                                if (title === "Invoices") {
                                    // Parse invoice data
                                    const invoiceDate = item.label?.replace("Invoice Date: ", "") || "";
                                    const valueParts = item.value?.split(" | ") || [];
                                    const amount = valueParts[0]?.replace("Amount: ", "") || "";
                                    const dueDate = valueParts[1]?.replace("Due: ", "") || "";
                                    
                                    return (
                                        <TableRow key={`invoice_${idx}`} sx={{ bgcolor: "#ffffff" }}>
                                            <TableCell sx={{ fontSize: { xs: 12, md: 14 }, color: "#374151" }}>
                                                {invoiceDate}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: { xs: 12, md: 14 }, color: "#374151" }}>
                                                {dueDate}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: { xs: 12, md: 14 }, color: "#374151", fontWeight: 600 }}>
                                                {amount}
                                            </TableCell>
                                        </TableRow>
                                    );
                                } else {
                                    // Parse payment data
                                    const paymentDate = item.label?.replace("Payment Date: ", "") || "";
                                    const paidAmount = item.value?.replace("Paid Amount: ", "") || "";
                                    
                                    return (
                                        <TableRow key={`payment_${idx}`} sx={{ bgcolor: "#ffffff" }}>
                                            <TableCell sx={{ fontSize: { xs: 12, md: 14 }, color: "#374151" }}>
                                                {paymentDate}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: { xs: 12, md: 14 }, color: "#374151", fontWeight: 600 }}>
                                                {paidAmount}
                                            </TableCell>
                                        </TableRow>
                                    );
                                }
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    return (
        <Paper
            elevation={0}
            sx={{
                width: { xs: "100%", md: "100%" },
                border: "1px solid #e5e7eb",
                borderRadius: { xs: 1, md: 2 },
                p: { xs: 1, md: 1.5 },
                bgcolor: "#ffffff",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: 12, md: 14 } }}>
                        {card?.title || "Billing Details"}
                    </Typography>
                    {card?.subtitle && (
                        <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>
                            {card.subtitle}
                        </Typography>
                    )}
                </Box>
            </Box>

            {(card?.sections || []).map((section: any, sIdx: number) => {
                const sectionTitle = section?.heading || "";
                return (
                    <Box key={`billing_section_${sIdx}`}>
                        {renderSection(section, sectionTitle)}
                    </Box>
                );
            })}

            {(card?.disclaimers || []).length > 0 && (
                <Typography sx={{ fontSize: { xs: 10, md: 11 }, color: "#6b7280", mt: 1 }}>
                    {card.disclaimers.join(" ")}
                </Typography>
            )}
        </Paper>
    );
};

export default BillingDetailsCard;